/**
 * TeleClaw API client
 * Handles all communication with the Worker backend
 */

import type {
  Agent,
  AgentListParams,
  AgentListResponse,
  CreateAgentRequest,
  CreateAgentResponse,
  VoteResponse,
} from '../types/agent';
import { getInitDataRaw } from './telegram';

// API base URL - configurable via env or defaults
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:8787'
    : window.location.hostname.includes('staging')
      ? 'https://teleclaw-api-staging.teleclaw.workers.dev'
      : 'https://teleclaw-api.teleclaw.workers.dev');

/**
 * API error with status code
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Make an authenticated request to the API
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Merge existing headers if any
  if (options.headers) {
    const existingHeaders = options.headers as Record<string, string>;
    Object.assign(headers, existingHeaders);
  }

  // Add initData for authentication if available
  const initData = getInitDataRaw();
  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError(
        `Request failed: ${response.statusText}`,
        response.status
      );
    }
    return {} as T;
  }

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(
      data.error || data.message || 'Request failed',
      response.status,
      data.code
    );
  }

  return data as T;
}

/**
 * Get list of agents with optional filtering
 */
export async function getAgents(
  params: AgentListParams = {}
): Promise<AgentListResponse> {
  const searchParams = new URLSearchParams();

  if (params.category) {
    searchParams.set('category', params.category);
  }
  if (params.sort) {
    searchParams.set('sort', params.sort);
  }
  if (params.page !== undefined) {
    searchParams.set('page', String(params.page));
  }
  if (params.limit !== undefined) {
    searchParams.set('limit', String(params.limit));
  }
  if (params.q) {
    searchParams.set('q', params.q);
  }
  if (params.featured) {
    searchParams.set('featured', 'true');
  }

  const query = searchParams.toString();
  const endpoint = `/api/agents${query ? `?${query}` : ''}`;

  return request<AgentListResponse>(endpoint);
}

/**
 * Get a single agent by ID
 */
export async function getAgent(id: string): Promise<Agent> {
  return request<Agent>(`/api/agents/${encodeURIComponent(id)}`);
}

/**
 * Vote for an agent (requires authentication)
 */
export async function vote(agentId: string): Promise<VoteResponse> {
  return request<VoteResponse>(
    `/api/agents/${encodeURIComponent(agentId)}/vote`,
    {
      method: 'POST',
    }
  );
}

/**
 * Remove vote from an agent (requires authentication)
 */
export async function unvote(agentId: string): Promise<VoteResponse> {
  return request<VoteResponse>(
    `/api/agents/${encodeURIComponent(agentId)}/vote`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Create a new agent listing (requires authentication)
 */
export async function createAgent(
  data: CreateAgentRequest
): Promise<CreateAgentResponse> {
  return request<CreateAgentResponse>('/api/agents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing agent listing
 */
export async function updateAgent(
  id: string,
  data: Partial<CreateAgentRequest>
): Promise<Agent> {
  return request<Agent>(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete an agent listing (requires authentication + ownership)
 */
export async function deleteAgent(id: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Analytics stats response
 */
export interface AnalyticsStats {
  views: number;
  clicks: number;
  shares: number;
}

/**
 * Get analytics stats for an agent
 */
export async function getStats(agentId: string): Promise<AnalyticsStats> {
  return request<AnalyticsStats>(
    `/api/agents/${encodeURIComponent(agentId)}/stats`
  );
}

/**
 * Check if API is reachable
 */
export async function healthCheck(): Promise<boolean> {
  try {
    await request<{ status: string }>('/api/health');
    return true;
  } catch {
    return false;
  }
}

/**
 * Upload response
 */
export interface UploadResponse {
  success: boolean;
  url: string;
  filename: string;
}

/**
 * Upload a logo image to R2
 * @param file - File object to upload
 * @returns URL of the uploaded image
 */
export async function uploadLogo(file: File): Promise<UploadResponse> {
  const url = `${API_BASE_URL}/api/upload`;

  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {};

  // Add initData for authentication if available
  const initData = getInitDataRaw();
  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(
      data.error || data.message || 'Upload failed',
      response.status,
      data.code
    );
  }

  return data as UploadResponse;
}

// ── Admin API ──

/**
 * Make an admin-authenticated request
 */
async function adminRequest<T>(
  endpoint: string,
  adminToken: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${adminToken}`,
  };

  if (options.headers) {
    Object.assign(headers, options.headers as Record<string, string>);
  }

  const response = await fetch(url, { ...options, headers });
  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(
      data.error || 'Request failed',
      response.status,
      data.code
    );
  }

  return data as T;
}

export interface AdminStats {
  total: number;
  verified: number;
  featured: number;
  listed: number;
  todaySubmissions: number;
}

export async function getAdminStats(adminToken: string): Promise<AdminStats> {
  return adminRequest<AdminStats>('/api/admin/stats', adminToken);
}

export async function getAdminAgents(
  adminToken: string,
  filter: string = 'all'
): Promise<{ agents: Agent[] }> {
  return adminRequest<{ agents: Agent[] }>(
    `/api/admin/agents?filter=${filter}&limit=200`,
    adminToken
  );
}

export async function setVerificationStatus(
  agentId: string,
  status: string,
  adminToken: string
): Promise<{ success: boolean }> {
  return adminRequest<{ success: boolean }>(
    `/api/agents/${encodeURIComponent(agentId)}/verify`,
    adminToken,
    {
      method: 'PUT',
      body: JSON.stringify({ verification_status: status }),
    }
  );
}

export async function adminDeleteAgent(
  agentId: string,
  adminToken: string
): Promise<{ success: boolean }> {
  return adminRequest<{ success: boolean }>(
    `/api/agents/${encodeURIComponent(agentId)}`,
    adminToken,
    { method: 'DELETE' }
  );
}

// ── Collections API ──

import type { Collection, Review, Referrer } from '../types/agent';

export async function getCollections(): Promise<{ collections: Collection[] }> {
  return request<{ collections: Collection[] }>('/api/collections');
}

export async function getCollection(slug: string): Promise<Collection> {
  return request<Collection>(`/api/collections/${encodeURIComponent(slug)}`);
}

export async function createCollection(
  data: { name: string; description?: string; emoji?: string; slug: string },
  adminToken: string
): Promise<{ id: string; slug: string }> {
  return adminRequest<{ id: string; slug: string }>('/api/collections', adminToken, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function addAgentToCollection(
  collectionId: string,
  agentId: string,
  adminToken: string
): Promise<{ success: boolean }> {
  return adminRequest<{ success: boolean }>(
    `/api/collections/${encodeURIComponent(collectionId)}/agents`,
    adminToken,
    { method: 'POST', body: JSON.stringify({ agent_id: agentId }) }
  );
}

export async function removeAgentFromCollection(
  collectionId: string,
  agentId: string,
  adminToken: string
): Promise<{ success: boolean }> {
  return adminRequest<{ success: boolean }>(
    `/api/collections/${encodeURIComponent(collectionId)}/agents/${encodeURIComponent(agentId)}`,
    adminToken,
    { method: 'DELETE' }
  );
}

// ── Reviews API ──

export async function getReviews(agentId: string): Promise<{ reviews: Review[] }> {
  return request<{ reviews: Review[] }>(`/api/agents/${encodeURIComponent(agentId)}/reviews`);
}

export async function submitReview(
  agentId: string,
  data: { rating: number; comment?: string }
): Promise<{ id: string; success: boolean }> {
  return request<{ id: string; success: boolean }>(
    `/api/agents/${encodeURIComponent(agentId)}/reviews`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function deleteReview(
  agentId: string,
  reviewId: string
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(
    `/api/agents/${encodeURIComponent(agentId)}/reviews/${encodeURIComponent(reviewId)}`,
    { method: 'DELETE' }
  );
}

// ── Referral API ──

export async function getReferralCode(agentId: string): Promise<{ referral_code: string }> {
  return request<{ referral_code: string }>(`/api/agents/${encodeURIComponent(agentId)}/referral-code`);
}

export async function getTopReferrers(limit = 20): Promise<{ referrers: Referrer[] }> {
  return request<{ referrers: Referrer[] }>(`/api/leaderboard/referrers?limit=${limit}`);
}

// Export API base URL for debugging
export { API_BASE_URL };
